import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ActionTypeEnum } from 'src/app/audit-log/action-type.enum';
import { AuditLogService } from 'src/app/audit-log/audit-log.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { FirestoreRulesService } from 'src/app/core/firestore-rules/firestore-rules.service';
import { AgeGroupEnum, LecturePriorityEnum, LectureStatusEnum, LectureTypeEnum, LectureValuesData, LocaleEnum } from 'src/app/submit-lecture/lecture.values';
import { CitiesService } from './cities/cities.service';
import { ICity } from './cities/city.interface';
import { ILecture } from './lecture.interface';
import { ILectureDetails } from './lecture.interface';
import { ISchool } from './schools/school.interface';
import { IFilter } from './find-lecture/components/find-lecture-filter';
import districts from '../../../../assets/data/districts.json';
import subdistricts from '../../../../assets/data/subdistricts.json';
import { ICountry } from './cities/country.interface';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING } from '../../../users/services/user.service';

const MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE = `MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE_${environment.production ? 'prod' : 'dev'}`;
const HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE = `HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE_${environment.production ? 'prod' : 'dev'}`;
const ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE = `All_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE_${environment.production ? 'prod' : 'dev'}`;

@Injectable({
  providedIn: 'root',
})
export class LecturesService {
  schoolsAndCitiesCombinedList: ISchool[] = [];
  searchLecturesResultsAndFilters: { lectures: ILecture[]; filters: IFilter } = null;

  constructor(
    private readonly authService: AuthService,
    private readonly firestoreRulesService: FirestoreRulesService,
    private readonly auditLogService: AuditLogService,
    private readonly citiesService: CitiesService,
    private readonly translate: TranslateService,
    private toastr: ToastrService
  ) {}

  getLecturesByUser(useCache = true): Promise<unknown> {
    let lastChangedDocs;

    return new Promise<unknown>(async (resolve, reject) => {
      try {
        let docs = null;
        let lastLectureUpdateDate = null;
        const user = await this.authService.getCurrentUser();
        if (useCache) {
          lastLectureUpdateDate = localStorage.getItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
          docs = await this.firestoreRulesService.getLecturesByUser(user.uid, true);
          // check if any lecture was updated since last cached
          if (docs && docs.length) {
            lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
          }
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (
          !docs ||
          docs.length === 0 ||
          !lastLectureUpdateDate ||
          (lastChangedDocs && lastChangedDocs.length &&
            this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateDate))
        ) {
          docs = await this.firestoreRulesService.getLecturesByUser(user.uid, false);
          if (lastChangedDocs) {
            const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
            if (changedDate) {
              localStorage.setItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
            }
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesByUserAndStatus(status: LectureStatusEnum): Promise<unknown> {
    let lastChangedDocs;

    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const lastLectureUpdateDate = localStorage.getItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
        const user = await this.authService.getCurrentUser();
        let docs = await this.firestoreRulesService.getLecturesByUserAndStatus(user.uid, status, true);
        // check if any lecture was updated since last cached
        if (docs && docs.length) {
          lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (
          !docs ||
          docs.length === 0 ||
          !lastLectureUpdateDate ||
          (lastChangedDocs && lastChangedDocs.length && this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateDate))
        ) {
          docs = await this.firestoreRulesService.getLecturesByUserAndStatus(user.uid, status, false);
          if (lastChangedDocs) {
            const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
            if (changedDate) {
              localStorage.setItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
            }
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesByUserIdAndStatus(uid: string, status: LectureStatusEnum): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const docs = await this.firestoreRulesService.getLecturesByUserAndStatus(uid, status, false);
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesByFilter(query: Function = undefined, forceCache = false): Promise<ILecture[]> {
    let lastChangedDocs;

    return new Promise<ILecture[]>(async (resolve, reject) => {
      try {
        const lastLectureUpdateFromLocalStorage = localStorage.getItem(ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
        let docs = await this.firestoreRulesService.getAllLectures(true, query);
        // check if any lecture was updated since last cached
        if (docs && docs.length) {
          lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (this.isRemoteDataFetching(forceCache, docs, lastLectureUpdateFromLocalStorage, lastChangedDocs)
          || docs?.length <= MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING) {
          docs = await this.firestoreRulesService.getAllLectures(false, query);
          if (lastChangedDocs && lastChangedDocs.length > 0) {
            const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
            if (changedDate) {
              localStorage.setItem(ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
            }
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesCompleteInDates(startDate: Date, endDate: Date): Promise<ILecture[]> {
    return new Promise<ILecture[]>(async (resolve, reject) => {
      try {
        const docs = await this.getLecturesInDates(startDate, endDate, 'scheduled_date');
        const statusDocs = docs.filter(l => {
          return l.status === LectureStatusEnum.COMPLETE;
        });
        resolve(statusDocs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesNotCompleteInDates(startDate: Date, endDate: Date): Promise<ILecture[]> {

    return new Promise<ILecture[]>(async (resolve, reject) => {
      try {
        const docs = await this.getLecturesInDates(startDate, endDate, 'created_at');
        const statusDocs = docs.filter(l => {
          return l.status !== LectureStatusEnum.COMPLETE && l.status !== LectureStatusEnum.IRRELEVANT;
        });
        resolve(statusDocs);
      } catch (error) {
        reject(error);
      }
    });
  }

  private getLecturesInDates(startDate: Date, endDate: Date, field: string): Promise<ILecture[]> {
    let lastChangedDocs;

    return new Promise<ILecture[]>(async (resolve, reject) => {
      try {
        const lastLectureUpdateDate = localStorage.getItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
        let docs = await this.firestoreRulesService.getLecturesInDates(startDate, endDate, field, true);
        // check if any lecture was updated since last cached
        if (docs && docs.length) {
          lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (
          !docs ||
          docs.length === 0 ||
          !lastLectureUpdateDate ||
          (lastChangedDocs && lastChangedDocs.length &&
            this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateDate))
        ) {
          docs = await this.firestoreRulesService.getLecturesInDates(startDate, endDate, field, false);
          if (lastChangedDocs) {
            const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
            if (changedDate) {
              localStorage.setItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
            }
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getAllLectures(query: Function = undefined, forceCache = false): Observable<ILecture[]> {
    const allLectures$ = from(this.getLecturesByFilter(query, forceCache));

    return combineLatest([allLectures$]).pipe(
      switchMap(([lectures]) =>
        combineLatest(lectures.map((x) => this.translate.stream([x.status, x.language, x.age_group, x.adultInstituteType, x.online_type, x.class]))).pipe(
          map((t) => ({
            translated: t.map((x) => Object.entries(x)),
            lectures,
          }))
        )
      ),
      map((x) =>
        x.lectures.map((l, i) => {
          l.metadata.translated = {
            status: x.translated[i][0] ? x.translated[i][0][1] : '',
            language: x.translated[i][1] ? x.translated[i][1][1] : '',
            age_group: x.translated[i][2] ? x.translated[i][2][1] : '',
            adultInstituteType: x.translated[i][3] ? x.translated[i][3][1] : '',
            online_type: x.translated[i][4] ? x.translated[i][4][1] : '',
            class: x.translated[i][5] ? x.translated[i][5][1] : '',
          };

          return l;
        })
      )
    );
  }

  getLecturesByUnassigned(): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const docs = await this.firestoreRulesService.getLecturesByStatus(LectureStatusEnum.UNASSIGNED);
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesBySkyzID(skyzId: string): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const docs = await this.firestoreRulesService.getLecturesBySkyzID(skyzId, false);
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesByUnassignedAndFilter(filterValues: any): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const locations = filterValues.prefs?.locations?.value;
        if (locations && locations.length > 10) {
          const first = locations.slice(0, 10);
          const second = locations.slice(10);
          const firstFilter = { ...filterValues };
          const secondFilter = { ...filterValues };
          firstFilter.prefs = { ...firstFilter.prefs };
          secondFilter.prefs = { ...secondFilter.prefs };
          firstFilter.prefs.locations = { key: 'address.subdistrict', value: first };
          secondFilter.prefs.locations = { key: 'address.subdistrict', value: second };

          const docs1 = (await this.getLecturesByUnassignedAndFilterAndMerge(firstFilter)) as [];
          const docs2 = (await this.getLecturesByUnassignedAndFilterAndMerge(secondFilter)) as [];

          const result = docs1.concat(docs2.filter((item) => !JSON.stringify(docs1).includes(JSON.stringify(item))));
          resolve(result);
        } else {
          resolve(this.getLecturesByUnassignedAndFilterAndMerge(filterValues));
        }
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  /* LOGICAL AND */
  getLecturesByUnassignedAndFilterAndMerge(filterValues: any): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const docs = (await this.firestoreRulesService.getLecturesByStatusAndFilter(LectureStatusEnum.UNASSIGNED, filterValues)) as [];
        let mergedArray = [];
        const resultKeysArr = [];
        docs.forEach((searchResult, _) => {
          mergedArray = [...mergedArray, ...searchResult];

          const keys = (searchResult as any).map((i) => {
            return i.metadata.docId;
          });
          resultKeysArr.push(keys);
        });

        let intersectionKeys = resultKeysArr[0];
        resultKeysArr.forEach((resultKeys) => {
          intersectionKeys = intersectionKeys.filter((x) => resultKeys.includes(x));
        });

        const set = new Set();
        const intersectionArr = mergedArray.filter((item) => {
          if (intersectionKeys.includes(item.metadata.docId) && !set.has(item.metadata.docId)) {
            set.add(item.metadata.docId);
            return true;
          }
          return false;
        }, set);

        console.log(intersectionArr);
        resolve(intersectionArr);
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  /* LOGICAL OR */
  getLecturesByUnassignedAndFilterOrMerge(docs: any): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        /* For intersection (AND) */
        let mergedArray = [];
        const resultKeysArr = [];
        docs.forEach((searchResult) => {
          mergedArray = [...mergedArray, ...searchResult];

          const keys = (searchResult as any).map((i) => {
            return i.metadata.docId;
          });
          resultKeysArr.push(keys);
        });

        let intersectionKeys = resultKeysArr[0];
        resultKeysArr.forEach((resultKeys) => {
          // intersectionKeys = intersectionKeys.filter(x => resultKeys.includes(x));
          intersectionKeys = [...new Set([...intersectionKeys, ...resultKeys])];
        });

        const set = new Set();
        const intersectionArr = mergedArray.filter((item) => {
          if (intersectionKeys.includes(item.metadata.docId) && !set.has(item.metadata.docId)) {
            set.add(item.metadata.docId);
            return true;
          }
          return false;
        }, set);

        resolve(intersectionArr);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLecturesByHighPriority(): Promise<unknown> {
    let lastChangedDocs;

    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const lastLectureUpdateDate = localStorage.getItem(HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
        let docs = await this.firestoreRulesService.getLecturesByStatusAndPriority(LectureStatusEnum.UNASSIGNED, LecturePriorityEnum.HIGH, true);
        // check if any lecture was updated since last cached
        if (docs && docs.length) {
          lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (
          !docs ||
          docs.length === 0 ||
          !lastLectureUpdateDate ||
          (lastChangedDocs && lastChangedDocs.length && this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateDate))
        ) {
          docs = await this.firestoreRulesService.getLecturesByStatusAndPriority(LectureStatusEnum.UNASSIGNED, LecturePriorityEnum.HIGH, false);
          if (lastChangedDocs) {
            const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
            if (changedDate) {
              localStorage.setItem(HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
            }
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getLectureById(docId): Promise<ILecture> {
    return new Promise<ILecture>(async (resolve, reject) => {
      try {
        const docs = await this.firestoreRulesService.getLectureById(docId);
        resolve(docs as ILecture);
      } catch (error) {
        reject(error);
      }
    });
  }

  getActiveClass(lecture: ILecture, activeLecture: ILecture): string {
    if (lecture.metadata.docId === activeLecture.metadata.docId) {
      return 'active';
    } else {
      return '';
    }
  }

  async setLastChangedLectureDate(): Promise<unknown> {
    const myLecturesLastChangedLectureDate = localStorage.getItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
    const hotLecturesLastChangedLectureDate = localStorage.getItem(HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
    if (!myLecturesLastChangedLectureDate || !hotLecturesLastChangedLectureDate) {
      const lastUpdatedLecture = await this.firestoreRulesService.getLastChangedLecture();
      if (lastUpdatedLecture && lastUpdatedLecture.length > 0) {
        const updatedLect = this.convertToDate(lastUpdatedLecture[0]?.updated_at);
        if (updatedLect) {
          localStorage.setItem(MY_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, updatedLect.toString());
          localStorage.setItem(HOT_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, updatedLect.toString());
        }
      }
    }
    return Promise.resolve();
  }

  async assignLecture(lecture: ILecture): Promise<unknown> {
    try {
      const user = await this.authService.getCurrentUser();
      const origLecture: ILecture = {
        ...lecture,
      };
      lecture.assigned_uid = user.uid;
      lecture.assigned_name = user?.displayName;
      lecture.assigned_at = new Date();
      lecture.status = LectureStatusEnum.ASSIGNED;
      const updatedLecture: ILecture = {
        ...lecture,
      };
      await this.updateLecture(updatedLecture, origLecture);
      return lecture;
    } catch (error) {
      throw error;
    }
  }

  async addLecture(lecture: ILecture, forceStatus?: boolean): Promise<void> {
    if (!forceStatus) {
      lecture.status = LectureStatusEnum.PENDING;
    }
    if (!lecture.created_at) {
      lecture.created_at = new Date();
    }
    lecture.priority = LecturePriorityEnum.NORMAL;
    this.addDistrictId(lecture);

    if (!lecture.type) {
      lecture.type = LectureTypeEnum.NONPAID;
    }

    return await this.firestoreRulesService
      .addLecture(lecture)
      .then((id) => {
        this.auditLogService.addLectureAuditLog(id, ActionTypeEnum.CREATED, lecture);
      })
      .catch((err) => {
        throw err;
      });
  }

  async deleteLecture(lectureId: string): Promise<void> {
    return await this.firestoreRulesService
      .deleteLecture(lectureId)
      .then((res) => {
        this.auditLogService.addLectureAuditLog(lectureId, ActionTypeEnum.DELETED, null); // TODO convert to ILecture
      })
      .catch((err) => {
        throw err;
      });
  }

  async updateLecture(lecture: ILecture, origLecture?: ILecture): Promise<void> {
    lecture.updated_at = new Date();
    const docId = lecture.metadata.docId;
    // do not save metadata in DB, and create object clone , to not delete metadata from original ref.
    lecture = Object.assign({}, { ...lecture }, { metadata: undefined });
    delete lecture.metadata;

    origLecture = Object.assign({}, { ...origLecture }, { metadata: undefined });
    delete origLecture.metadata;

    this.addDistrictId(lecture);

    return await this.firestoreRulesService
      .updateLecture(docId, lecture, origLecture)
      .then((res) => {
        this.auditLogService.addLectureAuditLog(docId, ActionTypeEnum.UPDATED, lecture, origLecture); // TODO convert to ILecture
      })
      .catch((err) => {
        throw err;
      });
  }

  private addDistrictId(lecture: ILecture): void {
    const city = lecture.address.city;
    const cityItem = this.citiesService.getCityById(city);
    if (cityItem) {
      lecture.address.district = Number(cityItem.district_id);
      lecture.address.subdistrict = Number(cityItem.subdistrict_id);
    }
  }

  updateLectureStatus(lecture: ILectureDetails): Promise<void> {
    return this.updateLecture(lecture);
  }

  updateLectureTime(lecture: ILectureDetails): Promise<void> {
    return this.updateLecture(lecture);
  }

  markLectureComplete(lecture: ILectureDetails): Promise<void> {
    lecture.status = LectureStatusEnum.COMPLETE;
    return this.updateLecture(lecture);
  }

  getLocDisplayName(lecture: ILecture, lectCity: ICity, lang: string): string {
    return (lecture.address.house_number ? lecture.address.house_number.toString() + ' ' : '') + lecture.address.street + ', ' + this.getCityDisplayName(lectCity, lang);
  }

  getStreetDisplayName(lecture: ILecture, lang: string): string {
    if (!lecture?.address?.street || lecture?.address?.street.length === 0) {
      return '';
    }

    switch (lang) {
      case LocaleEnum.EN:
        return (
          (lecture.address?.house_number ? lecture.address?.house_number + ' ' : '') +
          ' ' +
          lecture.address?.street +
          ' ' +
          this.translate.instant('LECTURE_DETAILS.STREET_LABEL') +
          ','
        );
        break;
      case LocaleEnum.HE:
        return (
          this.translate.instant('LECTURE_DETAILS.STREET_LABEL') +
          ' ' +
          lecture?.address?.street +
          ' ' +
          (lecture?.address?.house_number ? lecture?.address?.house_number + ' ' : '') +
          ','
        );
        break;
    }
  }

  getCityDisplayName(lectCity: ICity, lang: string): string {
    if (!lectCity) {
      return null;
    }
    if (lang === LocaleEnum.EN) {
      return lectCity.nameEN;
    } else {
      return lectCity.nameHE;
    }
  }

  getCountryDisplayName(lectCountry: ICountry, lang: string): string {
    if (!lectCountry) {
      return null;
    }
    if (lang === LocaleEnum.EN) {
      return lectCountry.nameEN;
    } else {
      return lectCountry.nameHE;
    }
  }

  getDistrictDisplayName(district: any, lang: string): string {
    const distItem = this.getDistrictById(district);
    if (!distItem) {
      return '';
    }
    if (lang === LocaleEnum.EN) {
      return distItem.nameEN;
    } else if (lang === LocaleEnum.HE) {
      return distItem.nameHE;
    } else if (lang === 'skyz') {
      if (distItem) {
        return distItem.skyz;
      } else {
        const otherDist = this.getDistrictById(7);
        return otherDist.skyz;
      }
    }
  }

  getSubDistrictDisplayName(subdistrict: any, lang: string): string {
    const subdistItem = this.getSubDistrictById(subdistrict);
    if (!subdistItem) {
      return '';
    }
    if (lang === LocaleEnum.EN) {
      return subdistItem.nameEN;
    } else if (lang === LocaleEnum.HE) {
      return subdistItem.nameHE;
    } else if (lang === 'skyz') {
      if (subdistItem) {
        return subdistItem.skyz;
      } else {
        const otherDist = this.getSubDistrictById(7);
        return otherDist.skyz;
      }
    }
  }

  getDistrictById(id: any): any {
    const filtered: any[] = districts.filter((d) => {
      return id === d.id;
    });

    return filtered[0];
  }

  getSubDistrictById(id: any): any {
    const filtered: any[] = subdistricts.filter((d) => {
      return id === d.id;
    });

    return filtered[0];
  }

  getCityInfo(id: string): any {
    const cityInfo: ICity = this.citiesService.getCityById(id);
    return cityInfo;
  }

  getStatus(status: string): LectureStatusEnum {
    const filtered: any[] = LectureValuesData.statuses.filter((st) => {
      return status.includes(st.nameEN) || status.includes(st.nameHE);
    });
    return filtered[0].id;
  }

  getSchoolDisplayName(lecture: ILecture, ageGroupInfo: any, lang: string): string {
    const school = lecture?.institute_name;
    const ageGroup = this.getAgeGroup(ageGroupInfo, lang);

    switch (lang) {
      case LocaleEnum.EN:
        return school + ' ' + ageGroup;
        break;
      case LocaleEnum.HE:
        return ageGroup + ' ' + school;
        break;
    }
  }

  getContactDisplayName(lecture: ILecture): string {
    let contact = lecture?.contact_name;
    contact += lecture?.contact_title ? ' (' + lecture?.contact_title + ') ' : '';

    return contact;
  }

  getAgeGroup(ageGroupInfo: any, lang: string): string {
    if (!ageGroupInfo) {
      return '';
    }

    if (lang === LocaleEnum.HE) {
      return ageGroupInfo.nameHE;
    } else {
      return ageGroupInfo.nameEN;
    }
  }

  getClassDisplayName(lecture: ILecture): string {
    return this.translate.instant('LECTURE_DETAILS.CLASS_LABEL') + ' ' + lecture.class;
  }

  getDateStr(d: any, lang: string): string {
    if (!d) {
      return;
    }

    const date = this.convertToDate(d);
    if (!date) {
      return;
    }

    if (lang === LocaleEnum.EN) {
      return date.getDate() + ' ' + this.getDisplayMonthEn(date) + ' ' + date.getFullYear();
    } else {
      return date.getDate() + ' ' + this.getDisplayMonthHe(date) + ' ' + date.getFullYear();
    }
  }

  getDisplayDay(d: any, lang: string): string {
    if (!d) {
      return;
    }

    const date = this.convertToDate(d);

    if (lang === LocaleEnum.EN) {
      return this.getDisplayDayEn(date);
    } else {
      return this.getDisplayDayHe(date);
    }
  }

  convertToDate(d: any): Date {
    if (!d) {
      return;
    }
    if (d instanceof Date) {
      return d;
    }

    if (new Date(d).getTime() > 0) {
      return new Date(d);
    }

    const seconds = (d as any).seconds;
    if (seconds) {
      const date = new Date(seconds * 1000);
      return date;
    }
    const day = (d as any).day;
    if (day) {
      const month = (d as any).month;
      const year = (d as any).year;

      const date = new Date(year, month, day);
      return date;
    }

    return null;
  }

  getDisplayDayEn(date: Date): string {
    let day: string;

    switch (date.getDay()) {
      case 0:
        day = 'Sunday';
        break;
      case 1:
        day = 'Monday';
        break;
      case 2:
        day = 'Tuesday';
        break;
      case 3:
        day = 'Wednesday';
        break;
      case 4:
        day = 'Thursday';
        break;
      case 5:
        day = 'Friday';
        break;
      case 6:
        day = 'Saturday';
    }
    return day;
  }

  getDisplayDayHe(date: Date): string {
    let day: string;

    switch (date.getDay()) {
      case 0:
        day = 'ראשון';
        break;
      case 1:
        day = 'שני';
        break;
      case 2:
        day = 'שלישי';
        break;
      case 3:
        day = 'רביעי';
        break;
      case 4:
        day = 'חמישי';
        break;
      case 5:
        day = 'שישי';
        break;
      case 6:
        day = 'שבת';
    }
    return day;
  }

  getDisplayMonthEn(date: Date): string {
    let month: string;

    switch (date.getMonth()) {
      case 0:
        month = 'Jan';
        break;
      case 1:
        month = 'Feb';
        break;
      case 2:
        month = 'Mar';
        break;
      case 3:
        month = 'Apr';
        break;
      case 4:
        month = 'May';
        break;
      case 5:
        month = 'Jun';
        break;
      case 6:
        month = 'Jul';
        break;
      case 7:
        month = 'Aug';
        break;
      case 8:
        month = 'Sep';
        break;
      case 9:
        month = 'Oct';
        break;
      case 10:
        month = 'Nov';
        break;
      case 11:
        month = 'Dec';
        break;
    }
    return month;
  }

  getDisplayMonthHe(date: Date): string {
    let month: string;

    switch (date.getMonth()) {
      case 0:
        month = 'ינו׳';
        break;
      case 1:
        month = 'פבר׳';
        break;
      case 2:
        month = 'מרץ';
        break;
      case 3:
        month = 'אפר׳';
        break;
      case 4:
        month = 'מאי';
        break;
      case 5:
        month = 'יונ׳';
        break;
      case 6:
        month = 'יול׳';
        break;
      case 7:
        month = 'אוג׳';
        break;
      case 8:
        month = 'ספט׳';
        break;
      case 9:
        month = 'אוק׳';
        break;
      case 10:
        month = 'נוב׳';
        break;
      case 11:
        month = 'דצמ׳';
        break;
    }
    return month;
  }

  getLangDisplayName(lecture: ILecture, lang: string): string {
    if (!lecture.language) {
      return null;
    }
    const filteredLang = LectureValuesData.languages.filter((language) => {
      return language.id === lecture.language;
    });

    if (!filteredLang || filteredLang.length === 0) {
      return lecture.language;
    }

    return lang === LocaleEnum.HE ? filteredLang[0].nameHE : filteredLang[0].nameEN;
  }

  getAgeGroupDisplayName(lecture: ILecture, lang: string): string {
    let displayStr = '';
    if (!lecture.age_group) {
      return null;
    }
    const filteredGroup = LectureValuesData.ageGroups.filter((group) => {
      return group.id === lecture.age_group;
    });
    if (!filteredGroup || filteredGroup.length === 0) {
      return null;
    }

    if (lang === LocaleEnum.HE) {
      displayStr = filteredGroup[0].nameHE;
    } else if (lang === LocaleEnum.EN) {
      displayStr = filteredGroup[0].nameEN;
    } else if (lang === 'skyz') {
      displayStr = filteredGroup[0].skyz;
    }

    if (lecture.age_group === AgeGroupEnum.ADULTS && lecture.adultInstituteType) {
      const filteredAdultsType = LectureValuesData.adultsInstituteTypes.filter((group) => {
        return group.id === lecture.adultInstituteType;
      });
      if (lang === LocaleEnum.HE) {
        displayStr += ' (' + filteredAdultsType[0].nameHE + ') ';
      } else if (lang === LocaleEnum.EN) {
        displayStr += ' (' + filteredAdultsType[0].nameEN + ') ';
      }
    }

    return displayStr;
  }

  getLanguageDisplayName(lecture: ILecture, lang: string): string {
    let displayStr = '';
    if (!lecture.language) {
      return null;
    }
    const filteredGroup = LectureValuesData.languages.filter((l) => {
      return l.id === lecture.language;
    });
    if (!filteredGroup || filteredGroup.length === 0) {
      return null;
    }

    if (lang === LocaleEnum.HE) {
      displayStr = filteredGroup[0].nameHE;
    } else if (lang === LocaleEnum.EN) {
      displayStr = filteredGroup[0].nameEN;
    }

    return displayStr;
  }

  getAdultsTypeDisplayName(lecture: ILecture, lang: string): string {
    let displayStr = '';
    if (lecture.age_group === AgeGroupEnum.ADULTS && lecture.adultInstituteType) {
      const filteredAdultsType = LectureValuesData.adultsInstituteTypes.filter((group) => {
        return group.id === lecture.adultInstituteType;
      });
      if (lang === LocaleEnum.HE) {
        displayStr = filteredAdultsType[0].nameHE;
      } else if (lang === LocaleEnum.EN) {
        displayStr = filteredAdultsType[0].nameEN;
      } else if (lang === 'skyz') {
        displayStr = filteredAdultsType[0].skyz;
      }
    }

    return displayStr;
  }

  getOnlineTypeDisplayName(lecture: ILecture, lang: string): string {
    let displayStr = '';
    const filteredType = LectureValuesData.types.filter((type) => {
      return type.id === lecture.online_type;
    });
    if (lang === LocaleEnum.HE) {
      displayStr = filteredType[0].nameHE;
    } else if (lang === LocaleEnum.EN) {
      displayStr = filteredType[0].nameEN;
    } else if (lang === 'skyz') {
      displayStr = filteredType[0].skyz;
    }

    return displayStr;
  }

  getPaidTypeDisplayName(lecture: ILecture, lang: string): string {
    const isPaid = lecture.type === LectureTypeEnum.PAID;

    return isPaid ? this.translate.instant('LECTURE_DETAILS.LABELS.PAID_YES') : this.translate.instant('LECTURE_DETAILS.LABELS.PAID_NO');
  }

  getStatusDisplayName(lecture: ILecture, lang: string): string {
    let displayStr = '';
    const filteredType = LectureValuesData.statuses.filter((status) => {
      return status.id === lecture.status;
    });

    if (filteredType[0]) {
      if (lang === LocaleEnum.HE) {
        displayStr = filteredType[0].nameHE;
      } else if (lang === LocaleEnum.EN) {
        displayStr = filteredType[0].nameEN;
      } else if (lang === 'skyz') {
        displayStr = filteredType[0].skyz;
      }
    }

    return displayStr;
  }

  getPriorityDisplayName(lecture: ILecture, lang: string): string {
    if (!lecture.priority) {
      return null;
    }
    const filteredGroup = LectureValuesData.priorities.filter((p) => {
      return p.id === lecture.priority;
    });

    return lang === LocaleEnum.HE ? filteredGroup[0].nameHE : filteredGroup[0].nameEN;
  }

  getTypeDisplayName(lecture: ILecture, lang: string): string {
    if (!lecture?.online_type) {
      return null;
    }
    const filteredType = LectureValuesData.types.filter((t) => {
      return t.id === lecture?.online_type;
    });

    return lang === LocaleEnum.HE ? filteredType[0].nameHE : filteredType[0].nameEN;
  }

  getLecturerName(lecture: ILecture): string {
    return lecture.assigned_name ? lecture.assigned_name : '';
  }

  getSchoolsAndCitiesCombinedList(): ISchool[] {
    if (this.schoolsAndCitiesCombinedList.length === 0) {
      this.schoolsAndCitiesCombinedList = LectureValuesData.schools.map((schoolItem) => {
        const schoolCityName = LectureValuesData.cities.find((city) => schoolItem.city === city.id);
        return { ...schoolItem, displayName: `${schoolItem.name}${schoolCityName ? `, ${schoolCityName.nameHE}` : ''}` };
      });
    }
    return this.schoolsAndCitiesCombinedList;
  }

  getAssignedUsers(): Promise<unknown> {
    return this.firestoreRulesService.getAssignedUsers();
  }

  setSearchLecturesFiltersAndResults(lectures: ILecture[], filters: IFilter): void {
    this.searchLecturesResultsAndFilters = { lectures, filters };
  }

  getSearchLecturesFiltersAndResults(): { lectures: ILecture[]; filters: IFilter } {
    return this.searchLecturesResultsAndFilters;
  }

  resetSearchLecturesFiltersAndResults(): void {
    this.searchLecturesResultsAndFilters = null;
  }

  getLecturesByCompleteStatus(): Promise<unknown> {
    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const user = await this.authService.getCurrentUser();
        const docs = await this.firestoreRulesService.getCompleteLecturesByUser(LectureStatusEnum.COMPLETE, user.uid);
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  private isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs: ILecture[], lastLectureUpdateFromLocalStorage: string): boolean {
    const parsedDate = new Date(lastLectureUpdateFromLocalStorage);
    const now = Date.now();
    const twoHours = 2 * 60 * 60 * 1000;
    const isMoreThanTwoHours = now - parsedDate.getTime() > twoHours;
    const lastChangedDocDate = this.convertToDate(lastChangedDocs[0].updated_at);
    return lastChangedDocDate > parsedDate || isMoreThanTwoHours;
  }

  private isRemoteDataFetching =
  (forceCache: boolean, docs: any[], lastLectureUpdateFromLocalStorage: string, lastChangedDocs: ILecture[]): boolean => {
    let result;
    result =
      !forceCache &&
      (!docs ||
        docs.length === 0 ||
        !lastLectureUpdateFromLocalStorage ||
        (lastChangedDocs && lastChangedDocs.length &&
          this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateFromLocalStorage)));
    return result;
  }

}
